import React, { useCallback, useState, useEffect, useReducer } from 'react';
import { View, Platform, StyleSheet, SafeAreaView, Text } from 'react-native';

import { useTranslation } from 'react-i18next';
import PickerSelect from '../PickerSelect';
import { CalendarList, LocaleConfig } from 'react-native-calendars';
import { Color, CommonStyles, CommonProperties } from '../../styles';
import moment from 'moment';
import { useComponentTKey, i18nNS } from '../../i18n';
import { useDispatch, useSelector } from 'react-redux';
import { openAlert } from '../../store/actions/alert';
import RowView from '../RowView';
import BottomButton from '../BottomButton';
import { selectLowFarePriceMap } from '../../store/selectors/search.selector';
import useCalendarLocaleConfig from '../../utils/useCalendarLocaleConfig';
import { EVENTS_ENABLED, REBOOK_ENABLED } from '../../config/env';
import { selectTezEvent } from '../../store/selectors/events.selector';
import { selectCompanyCurrencyCulture } from '../../store/selectors/auth.selector';
import { isBXcurrency } from '../../utils/currency.utils';
import { Modals } from '../../constants/ScreenNames';
import { selectCurrentExpenseReportCardModalItem } from '../../store/selectors/expense.selector';
import { setExpenseValue } from '../../store/actions/expense';

const hours = [
  { label: '12:00 AM', value: '0' },
  { label: '1:00 AM', value: '1' },
  { label: '2:00 AM', value: '2' },
  { label: '3:00 AM', value: '3' },
  { label: '4:00 AM', value: '4' },
  { label: '5:00 AM', value: '5' },
  { label: '6:00 AM', value: '6' },
  { label: '7:00 AM', value: '7' },
  { label: '8:00 AM', value: '8' },
  { label: '9:00 AM', value: '9' },
  { label: '10:00 AM', value: '10' },
  { label: '11:00 AM', value: '11' },
  { label: '12:00 PM', value: '12' },
  { label: '1:00 PM', value: '13' },
  { label: '2:00 PM', value: '14' },
  { label: '3:00 PM', value: '15' },
  { label: '4:00 PM', value: '16' },
  { label: '5:00 PM', value: '17' },
  { label: '6:00 PM', value: '18' },
  { label: '7:00 PM', value: '19' },
  { label: '8:00 PM', value: '20' },
  { label: '9:00 PM', value: '21' },
  { label: '10:00 PM', value: '22' },
  { label: '11:00 PM', value: '23' },
];

const carOptions = [
  { label: '15 mins', value: '15' },
  { label: '30 mins', value: '30' },
  { label: '45 mins', value: '45' },
  { label: '1 hr', value: '60' },
  { label: '1 hr 15 mins', value: '75' },
  { label: '1 hr 30 mins', value: '90' },
];

const setupStartMarker = (dispatch, day) => {
  const markedDates = {
    [day.dateString]: {
      startingDay: true,
      color: Color.primary,
      textColor: 'white',
      dateString: day.dateString,
    },
  };
  dispatch({
    type: 'set',
    isFromDatePicked: true,
    isToDatePicked: false,
    fromDate: day.dateString,
    toDate: '',
    markedDates: markedDates,
  });
};

const setupMarkedDates = (fromDate, toDate, markedDates) => {
  const mFromDate = moment(fromDate);
  const mToDate = moment(toDate);
  const range = mToDate.diff(mFromDate, 'days');
  if (range >= 0) {
    // same start and end date selected
    if (range == 0) {
      markedDates = {
        [toDate]: {
          color: Color.primary,
          textColor: 'white',
          startingDay: true,
          endingDay: true,
          dateString: mFromDate.format('YYYY-MM-DD'),
        },
      };
    } else {
      for (let i = 1; i <= range; i++) {
        let tempDate = mFromDate.add(1, 'days');
        tempDate = tempDate.format('YYYY-MM-DD');
        if (i < range) {
          markedDates[tempDate] = { color: Color.primary, textColor: 'white' };
        } else {
          markedDates[tempDate] = {
            endingDay: true,
            color: Color.primary,
            textColor: 'white',
            dateString: tempDate,
          };
        }
      }
    }
  }
  return [markedDates, range];
};

const setupInitialRange = (dispatch, fromDate, toDate) => {
  if (!fromDate || !toDate) {
    __DEV__ && console.warn(`setup inital range missing a date: from: ${fromDate} : to: ${toDate}`);
  }
  const markedDates = {
    [fromDate]: {
      startingDay: true,
      color: Color.primary,
      textColor: 'white',
      dateString: fromDate,
    },
  };
  const [mMarkedDates] = setupMarkedDates(fromDate, toDate, markedDates);
  dispatch({ type: 'set', markedDates: mMarkedDates, fromDate: fromDate });
};

const markedDatesInitialState = {
  isFromDatePicked: false,
  isToDatePicked: false,
  markedDates: {},
  fromDate: undefined,
};

function markedDatesReducer(state, action) {
  switch (action.type) {
    case 'set': {
      // eslint-disable-next-line no-unused-vars
      const { type } = action;
      return action;
    }

    default:
      throw new Error('invalid date action in marked dates reducer');
  }
}

const CalendarModal = ({ navigation, route }) => {
  LocaleConfig.locales['en'] = useCalendarLocaleConfig();
  LocaleConfig.defaultLocale = 'en';
  const { t } = useTranslation(i18nNS.searchWidget);
  const { tKey } = useComponentTKey('CalendarModal');
  const dispatch = useDispatch();
  const {
    allowRange,
    returnScreen,
    index,
    minDate,
    maxDate,
    pastScrollRange = 0,
    futureScrollRange = 11,
    fromDate,
    toDate,
    showCarTimePickers,
    isAirCar,
    isOneWay,
    dropOffTime,
    pickUpTime,
    inputName,
    isExpenseWidget,
    actionType,
    subTab = null,
    stateName,
  } = route.params || {};
  const lowFarePriceMap = useSelector(selectLowFarePriceMap);
  const currencyculture = useSelector(selectCompanyCurrencyCulture);
  const [localDropOffTime, setLocalDropOffTime] = useState('');
  const [localPickUpTime, setLocalPickUpTime] = useState('');
  const [arrivalCarOption, setArrivalCarOption] = useState('60');
  const [departureCarOption, setDepartureCarOption] = useState('60');
  const [markedDatesState, markedDatesDispatch] = useReducer(
    markedDatesReducer,
    markedDatesInitialState
  );
  const [today] = useState(moment(new Date()));
  const todayString = today.format('YYYY-MM-DD');
  const eventData = useSelector(selectTezEvent);
  // 👇️ passing function to useState
  const [localMaxDate] = useState(() => {
    if (EVENTS_ENABLED) {
      //set the events depart date
      return moment(eventData?.DepartureDateTimeFromEvent)
        .add(eventData.MeetingDateRange, 'days')
        .format('YYYY-MM-DD');
    } else if (REBOOK_ENABLED) {
      //rebook allows 7 days
      return maxDate || moment(today).add(6, 'days').format('YYYY-MM-DD');
    } else {
      //no restriction for tez & aa
      return maxDate || moment(today).add(331, 'days').format('YYYY-MM-DD');
    }
  });

  const [localMinDate] = useState(() => {
    if (EVENTS_ENABLED) {
      return moment(eventData?.ArrivalDateTimeToEvent)
        .add(-eventData.MeetingDateRange, 'days')
        .format('YYYY-MM-DD');
    } else {
      return route.params.isExpenseWidget ? 0 : minDate || todayString; //0
    }
  });
  const [currentHour] = useState(today.format('HH'));

  const initSelectedDates = () => {
    if (!allowRange) {
      const startDate = {
        selected: true,
        color: Color.primary,
        textColor: 'white',
        dateString: fromDate,
      };
      const markedDates = { [fromDate]: startDate };
      markedDatesDispatch({ type: 'set', markedDates });
    } else {
      setupInitialRange(markedDatesDispatch, fromDate, toDate);
    }
  };

  useEffect(initSelectedDates, []);

  const initCarTimes = () => {
    if (showCarTimePickers) {
      // if it's not already set - default it, else just use existing values
      if (!pickUpTime) {
        //if pickup date is today's date
        if (fromDate === todayString) {
          setTodaysTime();
        } else {
          setLocalPickUpTime('10');
          setLocalDropOffTime('14');
        }
      } else {
        if (fromDate === todayString) {
          setLocalDropOffTime(dropOffTime);
          setLocalPickUpTime(pickUpTime);
        } else {
          setLocalDropOffTime('11');
          setLocalPickUpTime('11');
        }
      }
    }
  };

  useEffect(initCarTimes, []);

  const setTodaysTime = () => {
    const sameDayDefault = +currentHour + 2;
    // for same day default to 2 hours away unless it would go into next day
    if (sameDayDefault < 24) {
      setLocalPickUpTime(sameDayDefault.toString());
    } else {
      setLocalPickUpTime('23');
    }
    setLocalDropOffTime('11');
  };

  const onDayPress = useCallback(
    (day) => {
      if (allowRange) {
        if (
          !markedDatesState.isFromDatePicked ||
          (markedDatesState.isFromDatePicked && markedDatesState.isToDatePicked)
        ) {
          setupStartMarker(markedDatesDispatch, day);
          if (day.dateString === todayString) {
            setTodaysTime();
          } else {
            setLocalDropOffTime('11');
            setLocalPickUpTime('11');
          }
        } else if (!markedDatesState.isToDatePicked) {
          const markedDates = { ...markedDatesState.markedDates };
          const [mMarkedDates, range] = setupMarkedDates(
            markedDatesState.fromDate,
            day.dateString,
            markedDates
          );
          if (range >= 0) {
            markedDatesDispatch({
              type: 'set',
              isFromDatePicked: true,
              isToDatePicked: true,
              markedDates: mMarkedDates,
            });
          } else {
            setupStartMarker(markedDatesDispatch, day);
          }
        }
      } else {
        const startDate = {
          selected: true,
          color: Color.primary,
          textColor: 'white',
          dateString: day.dateString,
        };
        const markedDates = { [day.dateString]: startDate };
        markedDatesDispatch({ type: 'set', markedDates });
      }
    },
    [allowRange, markedDatesState]
  );
  const currentExpenseReportCardModalItem = useSelector(selectCurrentExpenseReportCardModalItem);
  const onApplyHandler = useCallback(
    (e) => {
      // need to stop this from bubbling because it was activating the react navigation bottom bar and switching tabs
      e.preventDefault();
      const dates = Object.values(markedDatesState.markedDates);
      let startDate = dates.find((x) => x.startingDay);
      let endDate = '';
      if (allowRange) {
        endDate = dates.find((x) => x.endingDay);
      } else {
        startDate = dates[0];
      }

      if (allowRange && !endDate) {
        dispatch(openAlert({ message: t(tKey('PleasePickSecondDate')) }));
        return;
      }
      if (isExpenseWidget) {
        dispatch(
          setExpenseValue({ actionType, subTab, key: stateName, value: startDate?.dateString })
        );
        navigation.goBack();
        return;
      }
      navigation.navigate(returnScreen, {
        selectedDates: {
          inputName,
          criteriaIndex: index,
          startDate: startDate?.dateString,
          endDate: endDate?.dateString,
          dropOff: showCarTimePickers && !isAirCar ? localDropOffTime : departureCarOption,
          pickUp: showCarTimePickers && !isAirCar ? localPickUpTime : arrivalCarOption,
        },
        item: currentExpenseReportCardModalItem || null, // in order to maintain the same item when returining to the expense modal, we need to pass the item back.
      });
    },
    [
      t,
      tKey,
      inputName,
      dispatch,
      allowRange,
      markedDatesState.markedDates,
      navigation,
      returnScreen,
      index,
      localPickUpTime,
      localDropOffTime,
      departureCarOption,
      arrivalCarOption,
      isExpenseWidget,
    ]
  );

  // defining similiar classes and not using BaseText because it seemed to really hurt the performance of the calendar since there is so many nodes.
  const underDayView = useCallback(
    (date) => {
      if (
        returnScreen === Modals.TripExpenseAddModal ||
        !lowFarePriceMap ||
        isBXcurrency(currencyculture)
      ) {
        return null;
      }
      return (
        <Text style={lowFareClassMap[lowFarePriceMap[date.dateString]?.styleKey]}>
          {lowFarePriceMap[date.dateString]?.price}
        </Text>
      );
    },
    [lowFarePriceMap, currencyculture]
  );

  function getDiffInNo0fMonths() {
    if (Object.keys(markedDatesState?.markedDates)?.length > 0) {
      const dateStr = Object.keys(markedDatesState?.markedDates)?.[0];
      const selectedDate = moment(dateStr);
      if (dateStr !== todayString) {
        const currentDate = moment(todayString);
        const getDate = (date) => moment(date, 'DD/MM/YYYY')?.startOf('month');
        const diff = Math.abs(getDate(selectedDate)?.diff(getDate(currentDate), 'months'));
        return diff;
      } else {
        return 0;
      }
    }
  }

  // for events, adding default 5 years (60 months) for past scrolling
  const pastScrollDiff = route.params.isExpenseWidget ? 12 : getDiffInNo0fMonths() || 0;
  const md = markedDatesState?.markedDates && Object.keys(markedDatesState?.markedDates);
  const current = markedDatesState?.fromDate ? markedDatesState?.fromDate : md[0];
  return (
    <SafeAreaView style={styles.pageContainer}>
      <View style={CommonStyles.flex1}>
        {showCarTimePickers && (
          <RowView style={CommonStyles.m2}>
            <PickerSelect
              onValueChange={(value) => {
                setLocalPickUpTime(value);
              }}
              style={CommonStyles.mr2}
              items={hours}
              value={localPickUpTime}
              label={t(tKey('PickUpTime'))}
              labelStyle={CommonStyles.mb0}
            />

            <PickerSelect
              onValueChange={(value) => {
                setLocalDropOffTime(value);
              }}
              items={hours}
              value={localDropOffTime}
              label={t(tKey('DropOffTime'))}
              labelStyle={CommonStyles.mb0}
            />
          </RowView>
        )}

        {isAirCar && (
          <RowView style={CommonStyles.m2}>
            <PickerSelect
              onValueChange={(value) => {
                setArrivalCarOption(value);
              }}
              style={CommonStyles.mr2}
              items={carOptions}
              value={arrivalCarOption}
              label={t(tKey('CarAfterArrival'))}
              labelStyle={CommonStyles.mb0}
            />

            {!isOneWay && (
              <PickerSelect
                onValueChange={(value) => {
                  setDepartureCarOption(value);
                }}
                items={carOptions}
                value={departureCarOption}
                label={t(tKey('CarBeforeDeparture'))}
                labelStyle={CommonStyles.mb0}
              />
            )}
          </RowView>
        )}

        <View style={styles.calendarParentContainer}>
          <CalendarList
            key={current}
            current={current}
            pastScrollRange={pastScrollDiff}
            futureScrollRange={futureScrollRange}
            scrollEnabled={true}
            showScrollIndicator={true}
            minDate={localMinDate}
            maxDate={localMaxDate}
            onDayPress={(day) => onDayPress(day)}
            markedDates={markedDatesState.markedDates}
            markingType={'period'}
            theme={{
              textDayFontFamily: CommonProperties.fontFamily.fontFamily,
              textMonthFontFamily: CommonProperties.fontFamily.fontFamily,
              textDayHeaderFontFamily: CommonProperties.fontFamily.fontFamily,
            }}
            calendarHeight={lowFarePriceMap ? 465 : undefined}
            renderUnderDayView={underDayView}
          />
        </View>
        <BottomButton label={t('common:Apply')} onPress={onApplyHandler}></BottomButton>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  pageContainer: CommonProperties.defaultContainer,
  // not sure why overflowY is required on web in order to allow calendar to scroll
  calendarParentContainer: Platform.OS === 'web' ? { flex: 1, overflowY: 'scroll' } : { flex: 1 },
  performanceTextStyle: {
    ...CommonProperties.fontFamily,
    ...CommonProperties.body,
  },
  performanceTextStyleAboveAvg: {
    ...CommonProperties.fontFamily,
    ...CommonProperties.body,
    color: Color.primary,
  },
  performanceTextStyleBelowAvg: {
    ...CommonProperties.fontFamily,
    ...CommonProperties.body,
    color: Color.corporate,
  },
});

const lowFareClassMap = {
  below: styles.performanceTextStyleBelowAvg,
  equal: styles.performanceTextStyle,
  above: styles.performanceTextStyleAboveAvg,
};

export default CalendarModal;
